import React, { useState } from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import type { FinancialSettlement } from "@/lib/api/review";
import { DetailField } from "./Shared";
import { FilePreview } from "@/components/ui/FilePreview";

// ─── QR Code modal ────────────────────────────────────────────────────────────

function QrCodeModal({
  visible,
  onDismiss,
  qrCodeUrl,
  titleKey,
  applicationId,
}: {
  visible: boolean;
  onDismiss: () => void;
  qrCodeUrl: string;
  titleKey: TranslationKeys;
  applicationId: string;
}) {
  return (
    <Modal visible={visible} onDismiss={onDismiss} title={titleKey}>
      <FilePreview fileKey={qrCodeUrl} isVendor={false} applicationId={applicationId} />
    </Modal>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface FinancialSettlementTabProps {
  settlement: FinancialSettlement | undefined;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  applicationId: string;
}

export function FinancialSettlementTab({
  settlement,
  applicationId,
  t,
}: FinancialSettlementTabProps) {
  const notProvided = t("review.application.financialSettlement.notProvided");
  const [wechatQrVisible, setWechatQrVisible] = useState(false);

  function SectionHeader({ label }: { label: string }) {
    return (
      <AppText weight="bold" className="text-xs text-dark-text-15 uppercase tracking-widest mb-6">
        {label}
      </AppText>
    );
  }

  return (
    <View className="max-w-4xl items-start">
      <AppText weight="bold" className="text-xl text-dark-text-50 mb-2">
        {t("review.application.financialSettlement.title")}
      </AppText>
      <AppText className="text-base text-dark-text-20 mb-8 leading-6">
        {t("review.application.financialSettlement.description")}
      </AppText>

      <View className="h-px bg-stroke-20 mb-8 w-full" />

      {/* ── BANK ──────────────────────────────────────────────────── */}
      {settlement?.bank && (
        <View className="mb-10 items-start w-full">
          <SectionHeader label={t("review.application.financialSettlement.sections.bank")} />

          <DetailField label={t("review.application.financialSettlement.fields.accountHolderName")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {settlement.bank.accountHolderName}
            </AppText>
          </DetailField>

          <View className="flex-col tablet:flex-row w-full">
            <View className="tablet:flex-1 tablet:pe-6">
              <DetailField
                label={t("review.application.financialSettlement.fields.bankAccountNumber")}
              >
                <AppText weight="medium" className="text-base text-dark-text-50">
                  {settlement.bank.accountNumber}
                </AppText>
              </DetailField>
            </View>
          </View>

          <View className="flex-col tablet:flex-row w-full">
            <View className="tablet:flex-1 tablet:pe-6">
              <DetailField label={t("review.application.financialSettlement.fields.iban")}>
                {settlement.bank.iban ? (
                  <AppText weight="medium" className="text-base text-dark-text-50">
                    {settlement.bank.iban}
                  </AppText>
                ) : (
                  <AppText className="text-base text-dark-text-20 italic">{notProvided}</AppText>
                )}
              </DetailField>
            </View>
            <View className="tablet:flex-1 tablet:ps-6">
              <DetailField label={t("review.application.financialSettlement.fields.swiftCode")}>
                <AppText weight="medium" className="text-base text-dark-text-50">
                  {settlement.bank.swiftCode}
                </AppText>
              </DetailField>
            </View>
          </View>

          <DetailField label={t("review.application.financialSettlement.fields.bankAddress")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {settlement.bank.bankAddress}
            </AppText>
          </DetailField>

          <DetailField
            label={t("review.application.financialSettlement.fields.settlementCurrency")}
          >
            <AppText weight="medium" className="text-base text-dark-text-50">
              {settlement.bank.settlementCurrency}
            </AppText>
          </DetailField>
        </View>
      )}

      {/* ── ALIPAY ────────────────────────────────────────────────── */}
      {settlement?.alipay && (
        <View className="mb-10 items-start w-full">
          <SectionHeader label={t("review.application.financialSettlement.sections.alipay")} />

          <DetailField label={t("review.application.financialSettlement.fields.alipayMerchantId")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {settlement.alipay.merchantId}
            </AppText>
          </DetailField>

          <View className="flex-col tablet:flex-row w-full">
            <View className="tablet:flex-1 tablet:pe-6">
              <DetailField
                label={t("review.application.financialSettlement.fields.alipayBusinessName")}
              >
                <AppText weight="medium" className="text-base text-dark-text-50">
                  {settlement.alipay.businessName}
                </AppText>
              </DetailField>
            </View>
            <View className="tablet:flex-1 tablet:ps-6">
              <DetailField label={t("review.application.financialSettlement.fields.alipayEmail")}>
                <AppText weight="medium" className="text-base text-dark-text-50">
                  {settlement.alipay.email}
                </AppText>
              </DetailField>
            </View>
          </View>

          <DetailField
            label={t("review.application.financialSettlement.fields.settlementCurrency")}
          >
            <AppText weight="medium" className="text-base text-dark-text-50">
              {settlement.alipay.settlementCurrency}
            </AppText>
          </DetailField>
        </View>
      )}

      {/* ── WECHAT PAY ────────────────────────────────────────────── */}
      {settlement?.wechatPay && (
        <View className="mb-10 items-start w-full">
          <SectionHeader label={t("review.application.financialSettlement.sections.wechatPay")} />

          <DetailField label={t("review.application.financialSettlement.fields.wechatMerchantId")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {settlement.wechatPay.merchantId}
            </AppText>
          </DetailField>

          <View className="flex-col tablet:flex-row w-full">
            <View className="tablet:flex-1 tablet:pe-6">
              <DetailField
                label={t("review.application.financialSettlement.fields.wechatBusinessName")}
              >
                <AppText weight="medium" className="text-base text-dark-text-50">
                  {settlement.wechatPay.businessName}
                </AppText>
              </DetailField>
            </View>
            <View className="tablet:flex-1 tablet:ps-6">
              <DetailField label={t("review.application.financialSettlement.fields.wechatEmail")}>
                <AppText weight="medium" className="text-base text-dark-text-50">
                  {settlement.wechatPay.email}
                </AppText>
              </DetailField>
            </View>
          </View>

          <View className="flex-col tablet:flex-row w-full">
            <View className="tablet:flex-1 tablet:pe-6">
              <DetailField
                label={t(
                  "review.application.financialSettlement.fields.wechatSettlementAccountType"
                )}
              >
                <AppText weight="medium" className="text-base text-dark-text-50">
                  {settlement.wechatPay.settlementAccountType}
                </AppText>
              </DetailField>
            </View>
            <View className="tablet:flex-1 tablet:ps-6">
              <DetailField
                label={t("review.application.financialSettlement.fields.settlementCurrency")}
              >
                <AppText weight="medium" className="text-base text-dark-text-50">
                  {settlement.wechatPay.settlementCurrency}
                </AppText>
              </DetailField>
            </View>
          </View>

          {settlement.wechatPay.qrCodeUrl && (
            <DetailField label={t("review.application.financialSettlement.fields.wechatQrCode")}>
              <Button
                label="review.application.financialSettlement.showQrCode"
                onPress={() => setWechatQrVisible(true)}
                variant="primary"
                className="self-start"
              />
            </DetailField>
          )}
        </View>
      )}

      {settlement?.wechatPay?.qrCodeUrl && (
        <QrCodeModal
          visible={wechatQrVisible}
          onDismiss={() => setWechatQrVisible(false)}
          qrCodeUrl={settlement.wechatPay.qrCodeUrl}
          applicationId={applicationId}
          titleKey="review.application.financialSettlement.fields.wechatQrCode"
        />
      )}
    </View>
  );
}
